import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  IconButton,
  List,
  ListItem,
  Divider,
  Paper,
  MenuItem,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";

import { headers } from "../utils/utils";
import PopupMessage from "../ui/PopupMessage";

const ConfigScreen = () => {
  const screens = useMemo(
    () => ["Item Screen", "Order Screen", "Customer Screen"],
    [],
  ); // ✅ Memoize the array
  const [pages, setPages] = useState([]);

  const fetchCustomFields = useCallback(async () => {
    try {
      const fetchedPages = await Promise.all(
        screens.map(async (screen) => {
          const response = await fetch(
            `${process.env.REACT_APP_SYS_CONFIG_MGMT}/systemConfig/getDynamicFields?screenName=${screen}`,
            {
              method: "GET",
              headers: headers(),
            },
          );
          if (!response.ok) {
            throw new Error(`Failed to fetch fields for screen: ${screen}`);
          }
          const data = await response.json();
          return { title: screen, fields: data.length ? data : [] };
        }),
      );

      setPages(fetchedPages);
    } catch (error) {
      console.error("Error fetching custom fields:", error);
    }
  }, [screens, setPages]);

  useEffect(() => {
    fetchCustomFields(); // Fetch fields when component loads
  }, [fetchCustomFields]);

  const [messageOpen, setMessageOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");

  const handleAddField = (pageIndex) => {
    const updatedPages = pages.map((page, i) =>
      i === pageIndex
        ? {
            ...page,
            fields: [...page.fields, { label: "", inputType: "text" }],
          }
        : page,
    );
    setPages(updatedPages);
  };

  const handleRemoveField = (pageIndex, fieldIndex) => {
    const updatedPages = pages.map((page, i) =>
      i === pageIndex
        ? {
            ...page,
            fields: page.fields.filter((_, j) => j !== fieldIndex),
          }
        : page,
    );
    setPages(updatedPages);
  };

  const handleFieldChange = (pageIndex, fieldIndex, key, value) => {
    const updatedPages = pages.map((page, i) =>
      i === pageIndex
        ? {
            ...page,
            fields: page.fields.map((field, j) =>
              j === fieldIndex ? { ...field, [key]: value } : field,
            ),
          }
        : page,
    );
    setPages(updatedPages);
  };

  const handleSave = async () => {
    const payload = pages.map((page) => ({
      screenName: page.title,
      fields: page.fields,
    }));

    console.log("Payload to send:", payload);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SYS_CONFIG_MGMT}/systemConfig/saveFields`,
        {
          method: "POST",
          headers: headers(),
          body: JSON.stringify(payload),
        },
      );

      if (response.ok) {
        setMessageOpen(true);
        setMessage("Fields Saved Successfully");
        setMessageType("success");
      } else {
        setMessageOpen(true);
        setMessage("Failed");
        setMessageType("error");
      }
    } catch (error) {
      console.error("Error saving data:", error);
      alert("An error occurred while saving.");
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <List>
        {pages.map((page, pageIndex) => (
          <React.Fragment key={pageIndex}>
            <ListItem>
              <Paper sx={{ p: 2, width: "100%" }}>
                <Typography variant="h6" gutterBottom>
                  {page.title}
                </Typography>

                {page.fields.map((field, fieldIndex) => (
                  <Grid container spacing={2} key={fieldIndex} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        label="Label"
                        value={field.label}
                        onChange={(e) =>
                          handleFieldChange(
                            pageIndex,
                            fieldIndex,
                            "label",
                            e.target.value,
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        select
                        label="Input Type"
                        value={field.inputType}
                        onChange={(e) =>
                          handleFieldChange(
                            pageIndex,
                            fieldIndex,
                            "inputType",
                            e.target.value,
                          )
                        }
                      >
                        <MenuItem value="text">Text</MenuItem>
                        <MenuItem value="date">Date</MenuItem>
                        <MenuItem value="time">Time</MenuItem>
                        <MenuItem value="number">Number</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={2}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveField(pageIndex, fieldIndex)}
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}

                {/* Add Field Button */}
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleAddField(pageIndex)}
                >
                  Add Field
                </Button>
              </Paper>
            </ListItem>
            <Divider />
          </React.Fragment>
        ))}
      </List>

      <Box sx={{ textAlign: "right", mt: 4 }}>
        <Button
          variant="contained"
          color="success"
          onClick={handleSave}
          size="large"
        >
          Save
        </Button>
      </Box>
      <PopupMessage
        messageType={messageType}
        message={message}
        messageOpen={messageOpen}
      />
    </Box>
  );
};

export default ConfigScreen;
